import React from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Download, Edit } from 'lucide-react';
import { Button } from '../ui/button';

export function AppHeaderActions() {
  const location = useLocation();

  // Determine what actions to show based on route
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isUserShowPage = pathParts[0] === 'users' && pathParts[1] && pathParts[1] !== 'create';
  const isLocationShowPage = pathParts[0] === 'locations' && pathParts[1] && pathParts[1] !== 'create';
  const isReviewShowPage = pathParts[0] === 'reviews' && pathParts[1] && pathParts[1] !== 'create';
  const isFeedbackShowPage = pathParts[0] === 'feedback' && pathParts[1] && pathParts[1] !== 'create';
  const isShowPage = isUserShowPage || isLocationShowPage || isReviewShowPage || isFeedbackShowPage;

  // Only consider it a list page if it's NOT a show page
  const isUsersListPage = location.pathname === '/users';
  const isLocationsListPage = location.pathname === '/locations';
  const isReviewsListPage = location.pathname === '/reviews';
  const isSessionReviewsListPage = location.pathname === '/session-reviews';
  const isFeedbackListPage = location.pathname === '/feedback';
  const isDeviceTokensListPage = location.pathname === '/device-tokens';
  const isNotificationsListPage = location.pathname === '/notifications';
  const isNotificationLogsListPage = location.pathname === '/notification-logs';
  const isListPage = isUsersListPage || isLocationsListPage || isReviewsListPage || isSessionReviewsListPage || isFeedbackListPage || isDeviceTokensListPage || isNotificationsListPage || isNotificationLogsListPage;



  const handleCreate = () => {
    if (isUsersListPage) {
      window.location.href = '/#/users/create';
    } else if (isLocationsListPage) {
      window.location.href = '/#/locations/create';
    } else if (isReviewsListPage) {
      window.location.href = '/#/reviews/create';
    } else if (isFeedbackListPage) {
      window.location.href = '/#/feedback/create';
    } else if (isNotificationsListPage) {
      window.location.href = '/#/notifications/create';
    }
  };

  const handleExport = () => {
    // Simple CSV export functionality
    const fileName = `${getResourceName().toLowerCase().replace(' ', '-')}-export-${new Date().toISOString().split('T')[0]}.csv`;
    
    // For now, just trigger a download placeholder
    // In production, this would integrate with the actual data
    const csvContent = `data:text/csv;charset=utf-8,${getResourceName()} Export\nGenerated on: ${new Date().toLocaleDateString()}\n\nExport functionality will be implemented with actual data.`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = () => {
    const recordId = pathParts[1]; // The ID from the URL
    if (isUserShowPage && recordId) {
      window.location.href = `/#/users/${recordId}/edit`;
    } else if (isLocationShowPage && recordId) {
      window.location.href = `/#/locations/${recordId}/edit`;
    } else if (isReviewShowPage && recordId) {
      window.location.href = `/#/reviews/${recordId}/edit`;
    } else if (isFeedbackShowPage && recordId) {
      window.location.href = `/#/feedback/${recordId}/edit`;
    }
  };

  // Get the appropriate resource name for button titles
  const getResourceName = () => {
    if (isUsersListPage || isUserShowPage) return 'User';
    if (isLocationsListPage || isLocationShowPage) return 'Location';
    if (isReviewsListPage || isReviewShowPage) return 'Review';
    if (isSessionReviewsListPage) return 'Session Review';
    if (isFeedbackListPage || isFeedbackShowPage) return 'Feedback';
    if (isDeviceTokensListPage) return 'Device Token';
    if (isNotificationsListPage) return 'Notification';
    if (isNotificationLogsListPage) return 'Notification Log';
    return 'Item';
  };

  if (isShowPage) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleEdit}
        title={`Edit ${getResourceName()}`}
        className="h-8 w-8"
      >
        <Edit className="w-4 h-4" />
      </Button>
    );
  }

  if (!isListPage) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      {/* Create Button - Hide for device tokens since they're auto-created */}
      {!isDeviceTokensListPage && !isNotificationLogsListPage && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCreate}
          title={`Create ${getResourceName()}`}
          className="h-8 w-8"
        >
          <Plus className="w-4 h-4" />
        </Button>
      )}

      {/* Export Button - Hide for notifications since export doesn't make sense for broadcast notifications */}
      {!isNotificationsListPage && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleExport}
          title="Export"
          className="h-8 w-8"
        >
          <Download className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}